import { Temporal } from '@insio/dates';

/** Parses an ISO date/datetime x-value into epoch milliseconds (UTC). */
export function isoToEpochMs(value: string | number): number {
  if (typeof value === 'number') return value;
  // Full instant (has offset/Z) first, then plain date-times and dates.
  try {
    return Temporal.Instant.from(value).epochMilliseconds;
  } catch {
    return Temporal.PlainDateTime.from(value).toZonedDateTime('UTC').epochMilliseconds;
  }
}

/** A time tick — epoch ms plus the ISO string handed to formatters. */
export interface TimeTick {
  epochMs: number;
  iso: string;
}

/** Calendar units used for tick intervals. */
type TickUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

const MS = {
  second: 1000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000
} as const;

/** Tick intervals from seconds to years, ordered from finest to coarsest. */
const INTERVALS: { unit: TickUnit; amount: number; approxMs: number }[] = [
  { unit: 'second', amount: 1, approxMs: MS.second },
  { unit: 'second', amount: 15, approxMs: 15 * MS.second },
  { unit: 'minute', amount: 1, approxMs: MS.minute },
  { unit: 'minute', amount: 15, approxMs: 15 * MS.minute },
  { unit: 'hour', amount: 1, approxMs: MS.hour },
  { unit: 'hour', amount: 6, approxMs: 6 * MS.hour },
  { unit: 'day', amount: 1, approxMs: MS.day },
  { unit: 'week', amount: 1, approxMs: 7 * MS.day },
  { unit: 'month', amount: 1, approxMs: 30 * MS.day },
  { unit: 'month', amount: 3, approxMs: 91 * MS.day },
  { unit: 'year', amount: 1, approxMs: 365 * MS.day }
];

/** Floors a UTC datetime to the start of the given interval. */
function floorTo(
  dateTime: Temporal.ZonedDateTime,
  unit: TickUnit,
  amount: number
): Temporal.ZonedDateTime {
  switch (unit) {
    case 'year':
      return dateTime.with({ month: 1, day: 1 }).startOfDay();
    case 'month': {
      const month = Math.floor((dateTime.month - 1) / amount) * amount + 1;
      return dateTime.with({ month, day: 1 }).startOfDay();
    }
    case 'week': {
      const shift = dateTime.dayOfWeek - 1;
      return dateTime.subtract({ days: shift }).startOfDay();
    }
    case 'day':
      return dateTime.startOfDay();
    case 'hour':
      return dateTime.with({
        hour: Math.floor(dateTime.hour / amount) * amount,
        minute: 0,
        second: 0,
        millisecond: 0,
        microsecond: 0,
        nanosecond: 0
      });
    case 'minute':
      return dateTime.with({
        minute: Math.floor(dateTime.minute / amount) * amount,
        second: 0,
        millisecond: 0,
        microsecond: 0,
        nanosecond: 0
      });
    default:
      return dateTime.with({
        second: Math.floor(dateTime.second / amount) * amount,
        millisecond: 0,
        microsecond: 0,
        nanosecond: 0
      });
  }
}

function advance(
  dateTime: Temporal.ZonedDateTime,
  unit: TickUnit,
  amount: number
): Temporal.ZonedDateTime {
  return unit === 'week' ? dateTime.add({ weeks: amount }) : dateTime.add({ [`${unit}s`]: amount });
}

/** ISO representation for a tick — date-only when the interval is a day or coarser. */
function toIso(dateTime: Temporal.ZonedDateTime, unit: TickUnit): string {
  const coarse = unit === 'year' || unit === 'month' || unit === 'week' || unit === 'day';
  return coarse ? dateTime.toPlainDate().toString() : dateTime.toInstant().toString();
}

/**
 * Calendar-aware time ticks over an epoch-ms range (UTC).
 * Picks the finest interval yielding at most `count` ticks.
 */
export function buildTimeTicks(minMs: number, maxMs: number, count = 5): TimeTick[] {
  if (maxMs <= minMs) {
    const iso = Temporal.Instant.fromEpochMilliseconds(minMs).toString();
    return [{ epochMs: minMs, iso }];
  }

  const span = maxMs - minMs;
  const interval =
    INTERVALS.find(candidate => span / candidate.approxMs <= count) ??
    INTERVALS[INTERVALS.length - 1];
  const start = Temporal.Instant.fromEpochMilliseconds(minMs).toZonedDateTimeISO('UTC');

  const ticks: TimeTick[] = [];
  let cursor = floorTo(start, interval.unit, interval.amount);
  if (cursor.epochMilliseconds < minMs) {
    cursor = advance(cursor, interval.unit, interval.amount);
  }
  // For multi-year spans widen the year step so we stay within `count`.
  const yearStep =
    interval.unit === 'year' ? Math.max(Math.ceil(span / (365 * MS.day) / count), 1) : 1;

  while (cursor.epochMilliseconds <= maxMs) {
    ticks.push({ epochMs: cursor.epochMilliseconds, iso: toIso(cursor, interval.unit) });
    cursor = advance(cursor, interval.unit, interval.amount * yearStep);
  }
  return ticks;
}
